import { Card, Button } from '@design-system';
import {
  CreateQuoteButton,
  QuoteROIDisplay,
  QuoteDiscountSavingsDisplay,
  QuotePriceDisplay,
  QuoteProductInfo,
  formatGender,
  formatMaritalStatus,
  useTraditionalLTCPolicyManagement,
  useIllustrationManagement,
  useIllustrationPolling,
  type TraditionalLTCQuoteCardProps,
  type TraditionalLTCQuoteWithROI,
} from '@/features/shop-insurance';
import { ChevronUp } from 'lucide-react';
import { getPolicyTypeFromTraditionalLTCQuote } from '@shared/getPolicyTypeFromTraditionalLTCQuote';
import { cn } from '@/lib';
import { useSelector } from 'react-redux';
import { selectClientCalculationSettings } from '@/model/selectors';
import { PartnershipEligibilityDisclaimer } from '@client/components/PartnershipEligibilityDisclaimer';
import { PartnershipEligibleBadge } from '@client/components/PartnershipEligibleBadge';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';

export function TraditionalLTCQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  onClose,
  isSelected = false,
  fundingSourceId = null,
  testId,
  stateOfResidence,
}: TraditionalLTCQuoteCardProps) {
  useIllustrationPolling();

  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const annualHealthcareInflationRate =
    clientCalculationSettings?.healthCareInflationRate ?? undefined;

  const { handleAddPolicy, handleRemovePolicy } = useTraditionalLTCPolicyManagement({
    quote,
    fundingSourceId,
  });

  const { hasIllustrationPdf, isCreatingIllustration, handleViewPdf, handleCreateIllustration } =
    useIllustrationManagement({
      quote,
    });

  const displayPrice = quote.primaryModalPremium;
  const fundingOption = quote.premiumFrequency;
  const carrierName = quote.carrier;
  const quoteWithROI = quote as TraditionalLTCQuoteWithROI;
  const displayROI = quoteWithROI.formattedROI;
  const projectedCostCoverage = quoteWithROI.projectedCostCoverage;

  return (
    <>
      <Card
        data-testid={testId}
        className={cn(
          'hover:border-primary hover:ring-primary/10 w-full cursor-pointer gap-0 p-0 ring-4 ring-transparent transition-all duration-200',
          {
            'border-primary/50 bg-linear-to-r from-primary/5 to-primary/1 shadow-none': isSelected,
          },
        )}
        onClick={isSelected ? undefined : onToggleExpand}
      >
        <div className="relative">
          <div className="grid grid-cols-[1fr_auto] gap-4 p-4">
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
              <div className="flex flex-col gap-2">
                <QuoteProductInfo
                  carrierName={carrierName}
                  productName={quote.productName}
                  showLogo={true}
                  showRating={true}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <UnderwritingStatus
                    productName={quote.productName}
                    fundingPolicyType={quote.fundingPolicyType}
                  />
                  <PartnershipEligibleBadge
                    policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
                    state={stateOfResidence || quote.state}
                    primaryAge={quote.primaryAge}
                    bioPercent={quote.bioPercent}
                  />
                </div>
              </div>

              <QuoteROIDisplay
                projectedCostCoverage={projectedCostCoverage}
                policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
                displayROI={displayROI}
              />

              <QuoteDiscountSavingsDisplay
                discountSavings={quoteWithROI.discountSavings}
                discountPolicyType={quoteWithROI.discountPolicyType}
                policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
                carrierName={carrierName}
                annualHealthcareInflationRate={annualHealthcareInflationRate}
              />

              <QuotePriceDisplay
                price={displayPrice}
                fundingOption={fundingOption}
                label="Premium"
              />
            </div>

            <div className="flex shrink-0 flex-col gap-2 self-start">
              {isSelected ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="bg-destructive/10 text-destructive hover:bg-destructive/15"
                  onClick={handleRemovePolicy}
                >
                  Remove Product
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleAddPolicy}
                >
                  Select Product
                </Button>
              )}
              <CreateQuoteButton
                testId="shop-insurance-quote-download-button"
                onClick={
                  hasIllustrationPdf
                    ? handleViewPdf
                    : clickEvent => handleCreateIllustration(clickEvent, () => {})
                }
                disabled={isCreatingIllustration}
                isLoading={isCreatingIllustration}
                isCompleted={Boolean(hasIllustrationPdf)}
                defaultText="Create Quote"
                loadingText="Generating"
                completedText="View Quote"
                className="w-full"
              />
            </div>
          </div>
          {displayROI && (
            <div className="px-4 pb-4">
              <div className="bg-muted rounded-sm border px-3 py-2">
                <p className="text-muted-foreground text-xs">
                  * ROI includes projections of non-guaranteed network savings discounts. Actual
                  savings are subject to change, and network availability may vary.
                </p>
                <PartnershipEligibilityDisclaimer
                  policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
                  state={stateOfResidence || quote.state}
                  primaryAge={quote.primaryAge}
                  bioPercent={quote.bioPercent}
                  className="text-muted-foreground text-xs"
                />
              </div>
            </div>
          )}

          <div className="border-border mt-0 border-t">
            <div
              data-testid="shop-insurance-quote-expand-button"
              className={cn(
                'flex items-center justify-between p-4',
                !isSelected && 'cursor-pointer',
              )}
              onClick={clickEvent => {
                clickEvent.stopPropagation();
                onToggleExpand();
              }}
            >
              <span className="sm:text-md text-primary-dark select-none text-sm font-medium">
                Product Details
              </span>
              <ChevronUp
                className={cn('text-primary-dark size-4 transition-transform duration-200', {
                  'rotate-180': !isExpanded,
                })}
              />
            </div>
          </div>

          {isExpanded && (
            <div className="px-4 pb-4 [&>div]:mt-0">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-bold">Policy Details</h4>
                  <div className="space-y-1">
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Age: </span>
                      <span data-testid="quote-detail-age">{quote.primaryAge}</span>
                    </p>
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Gender: </span>
                      <span data-testid="quote-detail-gender">
                        {formatGender(quote.primaryGender)}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Marital Status: </span>
                      <span data-testid="quote-detail-marital-status">
                        {formatMaritalStatus(quote.maritalClass)}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Underwriting Class: </span>
                      <span data-testid="quote-detail-underwriting-class">
                        {quote.underwritingClass}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-bold">Benefits</h4>
                  <div className="space-y-1">
                    <p
                      className="text-primary-dark/90 text-sm leading-relaxed"
                      data-testid="quote-detail-benefit-pool"
                    >
                      <span className="font-medium">Benefit Pool: </span>$
                      {quote.benefitPool.toLocaleString()}
                    </p>
                    <p
                      className="text-primary-dark/90 text-sm leading-relaxed"
                      data-testid="quote-detail-daily-benefit"
                    >
                      <span className="font-medium">Daily Benefit: </span>$
                      {quote.dailyBenefitAmount}
                    </p>
                    <p
                      className="text-primary-dark/90 text-sm leading-relaxed"
                      data-testid="quote-detail-elimination-period"
                    >
                      <span className="font-medium">Elimination Period: </span>
                      {quote.eliminationPeriod} days
                    </p>
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Benefit Inflation Option: </span>
                      {quote.bioPercent === 'No BIO'
                        ? 'None'
                        : quote.bioPercent.replace(' BIO', '')}
                    </p>
                  </div>
                </div>

                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-bold">Premiums</h4>
                  <div className="space-y-1">
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Payment Frequency: </span>
                      {quote.premiumFrequency}
                    </p>
                    <p
                      className="text-primary-dark/90 text-sm leading-relaxed"
                      data-testid="quote-detail-premium"
                    >
                      <span className="font-medium">Modal Premium: </span>$
                      {quote.primaryModalPremium.toLocaleString()}
                    </p>
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Yearly Premium: </span>$
                      {quote.primaryTotalYearlyPremium.toLocaleString()}
                    </p>
                    {quote.dp1Rider && (
                      <p className="text-primary-dark/90 text-sm leading-relaxed">
                        &#10003; DP1 Rider
                      </p>
                    )}
                    {quote.nfoRider && (
                      <p className="text-primary-dark/90 text-sm leading-relaxed">
                        &#10003; NFO Rider
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
